// Filters-catalog-driven filter authoring for the scoring profile create/edit flow — an isolated
// Vuex module.
//
// Unlike `search`, there is no search/pagination/results concept here — this module only holds
// the catalog and the current filter values being authored for a single scoring profile.
import { get as _get } from '../../../utils/fetcher'

function withFilterValue(entries, key, value) {
  const others = entries.filter((f) => f.key !== key)
  // Same emptiness rules as search's withFilterValue: null/undefined/empty-array/all-null-range
  // means "not applied" and the entry is dropped entirely rather than sent with a null/empty value.
  const isEmptyRange =
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).every((v) => v === null || v === undefined)
  const isEmpty = value === null || value === undefined || (Array.isArray(value) && value.length === 0) || isEmptyRange
  return isEmpty ? others : [...others, { key, value }]
}

export default {
  namespaced: true,

  state: {
    catalog: { groups: [], ungrouped: [] },
    catalogLoading: false,
    filters: [],
  },

  mutations: {
    setCatalog(state, catalog) {
      state.catalog = catalog
    },
    setCatalogLoading(state, value) {
      state.catalogLoading = value
    },
    setFilters(state, filters) {
      state.filters = filters || []
    },
    setFilterValue(state, { key, value }) {
      state.filters = withFilterValue(state.filters, key, value)
    },
    resetFilters(state) {
      state.filters = []
    },
  },

  actions: {
    loadFiltersCatalog({ commit }) {
      commit('setCatalogLoading', true)
      return _get('/api/scoringProfiles/filters-catalog')
        .then((catalog) => commit('setCatalog', catalog))
        .finally(() => commit('setCatalogLoading', false))
    },

    setFilters({ commit }, filters) {
      commit('setFilters', filters)
    },

    setFilterValue({ commit }, payload) {
      commit('setFilterValue', payload)
    },

    resetFilters({ commit }) {
      commit('resetFilters')
    },

    // Same removal semantics as search's removeFilter/removeFilterValueItem/
    // removeFilterRangeSide, on this module's single `filters` array (no pending/applied split).
    removeFilter({ commit }, key) {
      commit('setFilterValue', { key, value: null })
    },

    removeFilterValueItem({ commit, state }, { key, item }) {
      const entry = state.filters.find((f) => f.key === key)
      if (!entry || !Array.isArray(entry.value)) {
        commit('setFilterValue', { key, value: null })
        return
      }
      const newValue = entry.value.filter((v) => JSON.stringify(v) !== JSON.stringify(item))
      commit('setFilterValue', { key, value: newValue })
    },

    removeFilterRangeSide({ commit, state }, { key, side }) {
      const entry = state.filters.find((f) => f.key === key)
      if (!entry || entry.value === null || typeof entry.value !== 'object' || Array.isArray(entry.value)) {
        commit('setFilterValue', { key, value: null })
        return
      }
      const newValue = { ...entry.value, [side]: null }
      const isEmpty = Object.values(newValue).every((v) => v === null || v === undefined)
      commit('setFilterValue', { key, value: isEmpty ? null : newValue })
    },
  },

  getters: {
    filterValue: (state) => (key) => {
      const entry = state.filters.find((f) => f.key === key)
      return entry ? entry.value : null
    },
    filtersPayload: (state) => state.filters,
  },
}
